import { type ReactNode, useEffect, useState } from 'react';

import { AnalyticsProvider as AnalyticsLibraryProvider } from 'use-analytics';
import type { AnalyticsPlugin, AnalyticsInstance } from 'analytics';

import MetaPixelPlugin from '../plugins/meta-pixel-plugin';
import ClarityPlugin from '../plugins/clarity-plugin';
import GTMPlugin from '../plugins/gtm-plugin';
import { analyticsSingleton } from '../utils/analytics-singleton';

import { useGetMerchantDetails } from '@/modules/stores/hooks/http/use-get-merchant-details';
import { useAnalyticsStoreSlug } from '../hooks/use-analytics-store-slug';

import { env } from '@/shared/lib/env';

import { GTMInjector } from '../components/gtm-injector';
import { MetaPageViewTracker } from '../components/meta-page-view-tracker';
interface AnalyticsProviderProps {
  children: ReactNode;
}

const NO_ANALYTICS = {} as AnalyticsInstance;

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const slug = useAnalyticsStoreSlug();

  const [analytics, setAnalytics] = useState<AnalyticsInstance | null>(null);

  const { data: merchant } = useGetMerchantDetails({ slug });

  useEffect(() => {
    console.log('[Analytics Provider] 🎯 Iniciando configuração', {
      merchantId: merchant?.id,
      hasAnalytics: !!merchant?.analytics,
      metaPixelId: merchant?.analytics?.meta_pixel_id,
      env: import.meta.env.MODE
    });

    if (!merchant?.analytics || !merchant.id) {
      console.log('[Analytics Provider] ⚠️ Sem dados de analytics ou merchant ID');
      setAnalytics(null);
      return;
    }

    const initializeAnalytics = async () => {
      const plugins: AnalyticsPlugin[] = [];

      // Meta Pixel - SEMPRE com debug: true para ver logs em produção também
      if (merchant.analytics.meta_pixel_id) {
        console.log('[Analytics Provider] 📱 Criando Meta Pixel Plugin', {
          pixelId: merchant.analytics.meta_pixel_id,
          mode: import.meta.env.MODE
        });
        plugins.push(
          new MetaPixelPlugin({
            pixelId: merchant.analytics.meta_pixel_id,
            options: {
              debug: true, // SEMPRE true para debugging
            },
          })
        );
      } else {
        console.log('[Analytics Provider] ⚠️ Meta Pixel ID não encontrado no merchant.analytics');
      }

      // Clarity
      if (env.VITE_CLARITY_PROJECT_ID) {
        plugins.push(
          new ClarityPlugin({
            projectId: env.VITE_CLARITY_PROJECT_ID,
            options: {
              debug: import.meta.env.MODE === 'development',
              track: true,
              lean: false,
              content: true,
            },
          })
        );
      }

      // GTM - SEMPRE com debug: true para verificar problemas
      if (merchant.analytics.google_tag_manager_id) {
        console.log('[Analytics Provider] 🏷️ Criando GTM Plugin', {
          containerId: merchant.analytics.google_tag_manager_id,
          mode: import.meta.env.MODE
        });
        plugins.push(
          new GTMPlugin({
            containerId: merchant.analytics.google_tag_manager_id,
            options: {
              debug: true, // SEMPRE true para debugging
            },
          })
        );
      } else {
        console.log('[Analytics Provider] ⚠️ GTM Container ID não encontrado no merchant.analytics');
      }

      console.log('[Analytics Provider] 📊 Total de plugins configurados:', plugins.length);

      if (plugins.length === 0) {
        console.log('[Analytics Provider] ⚠️ Nenhum plugin configurado, analytics desabilitado');
        setAnalytics(null);
        return;
      }

      try {
        console.log('[Analytics Provider] 🚀 Inicializando analytics singleton com plugins:', plugins.map(p => p.name));
        const analyticsInstance = await analyticsSingleton.getAnalytics({
          merchantId: merchant.id,
          plugins,
        });
        console.log('[Analytics Provider] ✅ Analytics inicializado com sucesso');

        // Verificar status do GTM após inicialização
        const gtmPlugin = plugins.find(p => p.name === 'google-tag-manager') as GTMPlugin;
        if (gtmPlugin && typeof gtmPlugin.checkGTMStatus === 'function') {
          setTimeout(() => {
            console.log('[Analytics Provider] 🔍 Verificando status do GTM após inicialização...');
            gtmPlugin.checkGTMStatus();
          }, 2000); // Aguardar 2 segundos para o GTM carregar
        }

        setAnalytics(analyticsInstance);
      } catch (error) {
        console.error('[Analytics Provider] ❌ Erro ao inicializar Analytics:', error);
        setAnalytics(null);
      }
    };

    initializeAnalytics();
  }, [merchant?.analytics, merchant?.id]);

  return (
    <>
      {/* Injetor do GTM - sempre ativo, mesmo sem analytics */}
      <GTMInjector />

      {/* PRAT-83: PageView (Pixel + API de Conversões) em toda troca de página. */}
      <MetaPageViewTracker slug={slug} />

      {/* PRAT-19: sempre o mesmo provider. Trocar `<>{children}</>` pelo
          provider quando o analytics ficava pronto remontava o app inteiro
          (estado da tela perdido e eventos duplicados). Sem instância, vai
          um objeto vazio: `analytics?.track?.()` vira no-op. */}
      <AnalyticsLibraryProvider instance={analytics ?? NO_ANALYTICS}>
        {children}
      </AnalyticsLibraryProvider>
    </>
  );
}